
import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
} from "@react-pdf/renderer";

/* ==========================================================================
   Excellent Kids Foundation
   TOP STUDENTS PER PERIOD REPORT

   A4 LANDSCAPE • PROFESSIONAL MULTI-PAGE REPORT

   Features:
   - Elegant navy, light blue, white and subtle gold palette.
   - Professional report header and information panels.
   - Class dux and top-three student highlights.
   - Full student ranking table.
   - Natural multi-page document flow.
   - Table rows never split between pages.
   - Repeating table column headings on continuation pages.
   - Fixed POLU footer at the bottom of every page.
   - Signature sections remain together where possible.
   - Preserves existing data structure and field names.
   ========================================================================== */

/* ==========================================================================
   SCHOOL BRANDING
   ========================================================================== */

const SCHOOL_NAME = "Excellent Kids Foundation";

const REPORT_TITLE = "TOP STUDENTS PER PERIOD REPORT";

const REPORT_SUBTITLE =
  "Academic Excellence & Student Ranking Report";

const SYSTEM_NAME =
  "POLU INTEGRATED SCHOOL MANAGEMENT SYSTEM";

/* ==========================================================================
   PROFESSIONAL COLOR PALETTE
   ========================================================================== */

const COLORS = {
  navy: "#102D50",
  navyDark: "#0A203A",
  blue: "#285B88",
  blueLight: "#EAF3FB",
  bluePale: "#F5F9FD",
  gold: "#C69A36",
  goldLight: "#F8F1DF",
  white: "#FFFFFF",
  background: "#F7F9FC",
  text: "#253447",
  textSecondary: "#637184",
  muted: "#8995A5",
  border: "#DCE5EF",
  borderDark: "#B9C9D9",
  success: "#216B4A",
  successLight: "#EAF5EE",
  grayLight: "#F1F4F8",
};

/* ==========================================================================
   PAGE CONFIGURATION
   ========================================================================== */

const PAGE_PADDING_X = 30;
const PAGE_PADDING_TOP = 27;
const PAGE_PADDING_BOTTOM = 43;

/* ==========================================================================
   PROFESSIONAL PDF STYLES
   ========================================================================== */

const styles = StyleSheet.create({
  /* PAGE */

  page: {
    size: "A4",
    paddingTop: PAGE_PADDING_TOP,
    paddingBottom: PAGE_PADDING_BOTTOM,
    paddingHorizontal: PAGE_PADDING_X,
    backgroundColor: COLORS.background,
    fontFamily: "Helvetica",
    fontSize: 9,
    color: COLORS.text,
  },

  /* TOP ACCENT */

  topAccent: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 5,
    backgroundColor: COLORS.navy,
  },

  goldAccent: {
    position: "absolute",
    top: 5,
    left: 0,
    width: "28%",
    height: 2,
    backgroundColor: COLORS.gold,
  },

  /* REPORT HEADER */

  header: {
    backgroundColor: COLORS.navy,
    borderRadius: 7,
    paddingVertical: 15,
    paddingHorizontal: 19,
    marginBottom: 13,
    borderBottomWidth: 3,
    borderBottomColor: COLORS.gold,
  },

  headerTopLine: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 5,
  },

  schoolName: {
    fontSize: 16,
    fontWeight: "bold",
    color: COLORS.white,
    textAlign: "center",
    letterSpacing: 0.45,
  },

  headerDivider: {
    width: 48,
    height: 2,
    backgroundColor: COLORS.gold,
    alignSelf: "center",
    marginTop: 7,
    marginBottom: 7,
  },

  reportTitle: {
    fontSize: 11,
    fontWeight: "bold",
    color: COLORS.white,
    textAlign: "center",
    letterSpacing: 1,
  },

  reportSubtitle: {
    fontSize: 8,
    color: "#DCE8F5",
    textAlign: "center",
    marginTop: 5,
  },

  headerMeta: {
    flexDirection: "row",
    justifyContent: "center",
    flexWrap: "wrap",
    marginTop: 9,
  },

  headerMetaText: {
    fontSize: 7,
    color: "#F0DCA9",
    textAlign: "center",
  },

  /* SECTION CONTAINERS */

  section: {
    backgroundColor: COLORS.white,
    borderWidth: 0.7,
    borderColor: COLORS.border,
    borderRadius: 6,
    padding: 11,
    marginBottom: 11,
  },

  sectionNoSplit: {
    backgroundColor: COLORS.white,
    borderWidth: 0.7,
    borderColor: COLORS.border,
    borderRadius: 6,
    padding: 11,
    marginBottom: 11,
  },

  sectionTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 9,
  },

  sectionAccent: {
    width: 3,
    height: 13,
    borderRadius: 2,
    backgroundColor: COLORS.gold,
    marginRight: 7,
  },

  sectionTitle: {
    fontSize: 9,
    fontWeight: "bold",
    color: COLORS.navy,
    letterSpacing: 0.4,
  },

  sectionDescription: {
    fontSize: 7,
    color: COLORS.textSecondary,
    marginTop: 3,
    marginBottom: 7,
    lineHeight: 1.35,
  },

  /* REPORT INFORMATION GRID */

  informationGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 7,
  },

  informationCard: {
    width: "32%",
    backgroundColor: COLORS.bluePale,
    borderWidth: 0.6,
    borderColor: COLORS.border,
    borderRadius: 4,
    paddingVertical: 8,
    paddingHorizontal: 9,
  },

  informationLabel: {
    fontSize: 6.8,
    color: COLORS.textSecondary,
    marginBottom: 4,
  },

  informationValue: {
    fontSize: 9,
    fontWeight: "bold",
    color: COLORS.navy,
  },

  informationValueSmall: {
    fontSize: 7.8,
    fontWeight: "bold",
    color: COLORS.navy,
  },

  /* SUMMARY ROWS */

  summaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 5,
    borderBottomWidth: 0.55,
    borderBottomColor: COLORS.border,
  },

  summaryRowLast: {
    borderBottomWidth: 0,
    paddingBottom: 1,
  },

  summaryLabel: {
    width: "43%",
    fontSize: 8,
    color: COLORS.textSecondary,
    fontWeight: "bold",
    paddingRight: 8,
  },

  summaryValue: {
    width: "57%",
    fontSize: 8.3,
    color: COLORS.text,
    textAlign: "right",
  },

  summaryValueStrong: {
    fontSize: 8.5,
    fontWeight: "bold",
    color: COLORS.navy,
  },

  /* TOP THREE STUDENTS */

  podiumContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "stretch",
    gap: 9,
    marginTop: 2,
  },

  podiumCard: {
    width: "32%",
    minHeight: 94,
    backgroundColor: COLORS.bluePale,
    borderWidth: 0.7,
    borderColor: COLORS.border,
    borderRadius: 6,
    paddingVertical: 10,
    paddingHorizontal: 8,
    alignItems: "center",
    justifyContent: "center",
  },

  podiumFirst: {
    backgroundColor: COLORS.goldLight,
    borderColor: "#D9BC75",
    borderTopWidth: 2.5,
    borderTopColor: COLORS.gold,
  },

  podiumSecond: {
    borderTopWidth: 2.5,
    borderTopColor: COLORS.blue,
  },

  podiumThird: {
    borderTopWidth: 2.5,
    borderTopColor: COLORS.borderDark,
  },

  podiumRank: {
    fontSize: 7,
    fontWeight: "bold",
    color: COLORS.blue,
    marginBottom: 5,
    letterSpacing: 0.5,
  },

  podiumRankFirst: {
    color: COLORS.navy,
  },

  podiumName: {
    fontSize: 8.5,
    fontWeight: "bold",
    color: COLORS.navy,
    textAlign: "center",
    lineHeight: 1.3,
    marginBottom: 5,
  },

  podiumClass: {
    fontSize: 7,
    color: COLORS.textSecondary,
    textAlign: "center",
    marginBottom: 5,
  },

  podiumAverage: {
    fontSize: 12,
    fontWeight: "bold",
    color: COLORS.navy,
  },

  podiumGrade: {
    fontSize: 7,
    fontWeight: "bold",
    color: COLORS.success,
    marginTop: 4,
  },

  podiumEmpty: {
    fontSize: 8,
    color: COLORS.muted,
    textAlign: "center",
  },

  /* RANKING TABLE */

  tableSection: {
    backgroundColor: COLORS.white,
    borderWidth: 0.7,
    borderColor: COLORS.border,
    borderRadius: 6,
    padding: 10,
    marginBottom: 11,
  },

  table: {
    width: "100%",
    borderWidth: 0.7,
    borderColor: COLORS.borderDark,
    borderRadius: 4,
  },

  tableHeader: {
    flexDirection: "row",
    alignItems: "stretch",
    backgroundColor: COLORS.navy,
    borderBottomWidth: 1.5,
    borderBottomColor: COLORS.gold,
  },

  tableRow: {
    flexDirection: "row",
    alignItems: "stretch",
  },

  tableRowAlternate: {
    backgroundColor: COLORS.bluePale,
  },

  tableCell: {
    paddingVertical: 7,
    paddingHorizontal: 7,
    borderRightWidth: 0.55,
    borderBottomWidth: 0.55,
    borderColor: COLORS.border,
    fontSize: 8,
    color: COLORS.text,
  },

  tableHeaderCell: {
    paddingVertical: 8,
    paddingHorizontal: 7,
    borderRightWidth: 0.55,
    borderRightColor: "#536A84",
    fontSize: 7.5,
    fontWeight: "bold",
    color: COLORS.white,
    textAlign: "left",
  },

  rankCell: {
    width: "10%",
    textAlign: "center",
  },

  studentCell: {
    width: "35%",
  },

  classCell: {
    width: "23%",
  },

  averageCell: {
    width: "17%",
    textAlign: "center",
  },

  gradeCell: {
    width: "15%",
    textAlign: "center",
    borderRightWidth: 0,
  },

  tableRankText: {
    fontWeight: "bold",
    color: COLORS.navy,
    textAlign: "center",
  },

  tableStudentText: {
    fontWeight: "bold",
    color: COLORS.text,
  },

  tableAverageText: {
    fontWeight: "bold",
    color: COLORS.navy,
    textAlign: "center",
  },

  tableGradeText: {
    fontWeight: "bold",
    color: COLORS.blue,
    textAlign: "center",
  },

  tableEmpty: {
    paddingVertical: 18,
    paddingHorizontal: 12,
    textAlign: "center",
    fontSize: 8.5,
    color: COLORS.textSecondary,
  },

  /* REPORT SUMMARY */

  summaryHighlight: {
    backgroundColor: COLORS.bluePale,
    borderWidth: 0.7,
    borderColor: COLORS.border,
    borderLeftWidth: 2.5,
    borderLeftColor: COLORS.gold,
    borderRadius: 4,
    padding: 10,
    marginBottom: 10,
  },

  summaryHighlightLabel: {
    fontSize: 7,
    color: COLORS.textSecondary,
    marginBottom: 4,
  },

  summaryHighlightValue: {
    fontSize: 11,
    fontWeight: "bold",
    color: COLORS.navy,
  },

  /* SIGNATURES */

  signatureSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
    marginTop: 18,
    marginBottom: 12,
  },

  signatureBox: {
    width: "38%",
    alignItems: "center",
  },

  signatureLine: {
    width: "100%",
    height: 20,
    borderBottomWidth: 0.8,
    borderBottomColor: COLORS.navy,
    marginBottom: 6,
  },

  signatureText: {
    fontSize: 8,
    fontWeight: "bold",
    color: COLORS.navy,
    textAlign: "center",
  },

  signatureSubtext: {
    fontSize: 6.8,
    color: COLORS.textSecondary,
    textAlign: "center",
    marginTop: 3,
  },

  /* CONFIDENTIALITY NOTE */

  confidentiality: {
    backgroundColor: COLORS.grayLight,
    borderRadius: 3,
    paddingVertical: 7,
    paddingHorizontal: 9,
    marginTop: 3,
    marginBottom: 7,
  },

  confidentialityText: {
    fontSize: 7,
    color: COLORS.textSecondary,
    textAlign: "center",
    lineHeight: 1.4,
  },

  /* EVERY-PAGE FOOTER */

  pageFooter: {
    position: "absolute",
    left: PAGE_PADDING_X,
    right: PAGE_PADDING_X,
    bottom: 13,
    borderTopWidth: 0.8,
    borderTopColor: COLORS.borderDark,
    paddingTop: 6,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },

  footerLeft: {
    width: "65%",
    fontSize: 6.5,
    color: COLORS.textSecondary,
  },

  footerRight: {
    width: "35%",
    fontSize: 7,
    fontWeight: "bold",
    color: COLORS.navy,
    textAlign: "right",
  },

  footerBrand: {
    fontSize: 6.5,
    fontWeight: "bold",
    color: COLORS.navy,
  },
});

/* ==========================================================================
   HELPERS
   ========================================================================== */

const displayValue = (value, fallback = "--") => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  return String(value);
};

const formatAverage = (value) => {
  if (value === null || value === undefined || value === "") {
    return "--";
  }

  return `${value}%`;
};

const getReportDate = (data) => {
  if (data?.generated_at) {
    const date = new Date(data.generated_at);

    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleDateString();
    }
  }

  return new Date().toLocaleDateString();
};

/* ==========================================================================
   SECTION HEADING
   ========================================================================== */

const SectionHeading = ({ title, description }) => (
  <View>
    <View style={styles.sectionTitleRow}>
      <View style={styles.sectionAccent} />

      <Text style={styles.sectionTitle}>
        {title}
      </Text>
    </View>

    {description ? (
      <Text style={styles.sectionDescription}>
        {description}
      </Text>
    ) : null}
  </View>
);

/* ==========================================================================
   SUMMARY ITEM
   ========================================================================== */

const SummaryItem = ({ label, value, last = false }) => (
  <View
    style={[
      styles.summaryRow,
      last && styles.summaryRowLast,
    ]}
  >
    <Text style={styles.summaryLabel}>
      {label}
    </Text>

    <Text
      style={[
        styles.summaryValue,
        styles.summaryValueStrong,
      ]}
    >
      {displayValue(value)}
    </Text>
  </View>
);

/* ==========================================================================
   REPORT INFORMATION CARD
   ========================================================================== */

const InformationCard = ({ label, value, small = false }) => (
  <View style={styles.informationCard}>
    <Text style={styles.informationLabel}>
      {label}
    </Text>

    <Text
      style={
        small
          ? styles.informationValueSmall
          : styles.informationValue
      }
    >
      {displayValue(value)}
    </Text>
  </View>
);

/* ==========================================================================
   TOP THREE STUDENTS
   ========================================================================== */

const PodiumStudent = ({
  student,
  rank,
}) => {
  const rankStyle =
    rank === 1
      ? styles.podiumFirst
      : rank === 2
        ? styles.podiumSecond
        : styles.podiumThird;

  if (!student) {
    return (
      <View
        style={[
          styles.podiumCard,
          rankStyle,
        ]}
        wrap={false}
      >
        <Text style={styles.podiumRank}>
          {rank === 1
            ? "1ST PLACE"
            : rank === 2
              ? "2ND PLACE"
              : "3RD PLACE"}
        </Text>

        <Text style={styles.podiumEmpty}>
          No student data
        </Text>
      </View>
    );
  }

  return (
    <View
      style={[
        styles.podiumCard,
        rankStyle,
      ]}
      wrap={false}
    >
      <Text
        style={[
          styles.podiumRank,
          rank === 1 && styles.podiumRankFirst,
        ]}
      >
        {rank === 1
          ? "1ST PLACE"
          : rank === 2
            ? "2ND PLACE"
            : "3RD PLACE"}
      </Text>

      <Text style={styles.podiumName}>
        {displayValue(student.student_name)}
      </Text>

      <Text style={styles.podiumClass}>
        {displayValue(student.grade_class)}
      </Text>

      <Text style={styles.podiumAverage}>
        {formatAverage(student.average_score)}
      </Text>

      <Text style={styles.podiumGrade}>
        Grade: {displayValue(student.alphabet_grade)}
      </Text>
    </View>
  );
};

/* ==========================================================================
   RANKING TABLE HEADER

   The fixed header is positioned in the table's normal flow.
   It repeats on pages where the ranking table continues.
   ========================================================================== */

const RankingTableHeader = () => (
  <View style={styles.tableHeader} fixed>
    <Text
      style={[
        styles.tableHeaderCell,
        styles.rankCell,
      ]}
    >
      RANK
    </Text>

    <Text
      style={[
        styles.tableHeaderCell,
        styles.studentCell,
      ]}
    >
      STUDENT NAME
    </Text>

    <Text
      style={[
        styles.tableHeaderCell,
        styles.classCell,
      ]}
    >
      GRADE CLASS
    </Text>

    <Text
      style={[
        styles.tableHeaderCell,
        styles.averageCell,
      ]}
    >
      AVERAGE
    </Text>

    <Text
      style={[
        styles.tableHeaderCell,
        styles.gradeCell,
      ]}
    >
      GRADE
    </Text>
  </View>
);

/* ==========================================================================
   RANKING TABLE ROW

   wrap={false} keeps each student record together.
   ========================================================================== */

const RankingTableRow = ({
  student,
  index,
}) => (
  <View
    style={[
      styles.tableRow,
      index % 2 === 1 && styles.tableRowAlternate,
    ]}
    wrap={false}
  >
    <Text
      style={[
        styles.tableCell,
        styles.rankCell,
        styles.tableRankText,
      ]}
    >
      {displayValue(student.rank, String(index + 1))}
    </Text>

    <Text
      style={[
        styles.tableCell,
        styles.studentCell,
        styles.tableStudentText,
      ]}
    >
      {displayValue(student.student_name)}
    </Text>

    <Text
      style={[
        styles.tableCell,
        styles.classCell,
      ]}
    >
      {displayValue(student.grade_class)}
    </Text>

    <Text
      style={[
        styles.tableCell,
        styles.averageCell,
        styles.tableAverageText,
      ]}
    >
      {formatAverage(student.average_score)}
    </Text>

    <Text
      style={[
        styles.tableCell,
        styles.gradeCell,
        styles.tableGradeText,
      ]}
    >
      {displayValue(student.alphabet_grade)}
    </Text>
  </View>
);

/* ==========================================================================
   MAIN PDF COMPONENT
   ========================================================================== */

const TopStudentPerPeriodPDF = ({ data }) => {
  if (!data) {
    return null;
  }

  const students = Array.isArray(data.students)
    ? data.students
    : [];

  /*
   * Use the first three entries in the existing ranked list.
   * The component does not change or reorder the backend ranking.
   */

  const dux = students[0] || null;
  const secondPlace = students[1] || null;
  const thirdPlace = students[2] || null;

  const reportDate = getReportDate(data);

  return (
    <Document
      title={`${SCHOOL_NAME} - Top Students Per Period Report`}
      author={SCHOOL_NAME}
      subject="Academic Excellence and Student Ranking Report"
      creator={SYSTEM_NAME}
      producer="React PDF"
    >
      <Page
        size="A4"
        orientation="landscape"
        style={styles.page}
        wrap
      >
        {/* PAGE BRANDING ACCENTS */}

        <View style={styles.topAccent} fixed />
        <View style={styles.goldAccent} fixed />

        {/* REPORT HEADER */}

        <View
          style={styles.header}
          wrap={false}
        >
          <View style={styles.headerTopLine}>
            <Text style={styles.schoolName}>
              {SCHOOL_NAME}
            </Text>
          </View>

          <View style={styles.headerDivider} />

          <Text style={styles.reportTitle}>
            {REPORT_TITLE}
          </Text>

          <Text style={styles.reportSubtitle}>
            {REPORT_SUBTITLE}
          </Text>

          <View style={styles.headerMeta}>
            <Text style={styles.headerMetaText}>
              {displayValue(data.grade_class)}
              {"   •   "}
              {displayValue(data.period)}
              {"   •   "}
              {displayValue(data.academic_year)}
            </Text>
          </View>
        </View>

        {/* REPORT INFORMATION */}

        <View
          style={styles.sectionNoSplit}
          wrap={false}
        >
          <SectionHeading title="REPORT INFORMATION" />

          <View style={styles.informationGrid}>
            <InformationCard
              label="ACADEMIC YEAR"
              value={data.academic_year}
            />

            <InformationCard
              label="GRADE CLASS"
              value={data.grade_class}
            />

            <InformationCard
              label="ACADEMIC PERIOD"
              value={data.period}
            />

            <InformationCard
              label="TOTAL RANKED STUDENTS"
              value={students.length}
            />

            <InformationCard
              label="REPORT GENERATED"
              value={reportDate}
              small
            />

            <InformationCard
              label="HIGHEST AVERAGE"
              value={
                dux
                  ? formatAverage(dux.average_score)
                  : "--"
              }
            />
          </View>
        </View>

        {/* CLASS DUX */}

        <View
          style={styles.sectionNoSplit}
          wrap={false}
        >
          <SectionHeading
            title="CLASS DUX"
            description="Leading academic performer for the selected period."
          />

          {dux ? (
            <>
              <SummaryItem
                label="Student Name"
                value={dux.student_name}
              />

              <SummaryItem
                label="Rank"
                value={`#${displayValue(dux.rank, "1")}`}
              />

              <SummaryItem
                label="Average Score"
                value={formatAverage(dux.average_score)}
              />

              <SummaryItem
                label="Alphabet Grade"
                value={dux.alphabet_grade}
                last
              />
            </>
          ) : (
            <Text style={styles.sectionDescription}>
              No ranking data is available for this report.
            </Text>
          )}
        </View>

        {/* TOP THREE STUDENTS */}

        <View
          style={styles.sectionNoSplit}
          wrap={false}
        >
          <SectionHeading
            title="TOP THREE STUDENTS"
            description="Recognizing the three highest-ranked students."
          />

          <View style={styles.podiumContainer}>
            <PodiumStudent
              student={dux}
              rank={1}
            />

            <PodiumStudent
              student={secondPlace}
              rank={2}
            />

            <PodiumStudent
              student={thirdPlace}
              rank={3}
            />
          </View>
        </View>

        {/* COMPLETE STUDENT RANKING */}

        <View style={styles.tableSection}>
          <SectionHeading
            title="COMPLETE STUDENT RANKING"
            description="Official student ranking register for the selected academic period."
          />

          <View style={styles.table}>
            <RankingTableHeader />

            {students.length > 0 ? (
              students.map((student, index) => (
                <RankingTableRow
                  key={`${student.rank ?? index}-${student.student_name ?? "student"}`}
                  student={student}
                  index={index}
                />
              ))
            ) : (
              <Text style={styles.tableEmpty}>
                No student ranking records are available.
              </Text>
            )}
          </View>
        </View>

        {/* FINAL REPORT SUMMARY */}

        <View
          style={styles.sectionNoSplit}
          wrap={false}
        >
          <SectionHeading title="REPORT SUMMARY" />

          <View style={styles.summaryHighlight}>
            <Text style={styles.summaryHighlightLabel}>
              CLASS DUX
            </Text>

            <Text style={styles.summaryHighlightValue}>
              {displayValue(dux?.student_name)}
            </Text>
          </View>

          <SummaryItem
            label="Total Ranked Students"
            value={students.length}
          />

          <SummaryItem
            label="Highest Average"
            value={
              dux
                ? formatAverage(dux.average_score)
                : "--"
            }
          />

          <SummaryItem
            label="Academic Period"
            value={data.period}
          />

          <SummaryItem
            label="Academic Year"
            value={data.academic_year}
            last
          />
        </View>

        {/* SIGNATURES */}

        <View
          style={styles.signatureSection}
          wrap={false}
        >
          <View style={styles.signatureBox}>
            <View style={styles.signatureLine} />

            <Text style={styles.signatureText}>
              Class Teacher
            </Text>

            <Text style={styles.signatureSubtext}>
              Signature / Date
            </Text>
          </View>

          <View style={styles.signatureBox}>
            <View style={styles.signatureLine} />

            <Text style={styles.signatureText}>
              Academic Director / Principal
            </Text>

            <Text style={styles.signatureSubtext}>
              Signature / Date
            </Text>
          </View>

          <View style={styles.signatureBox}>
            <View style={styles.signatureLine} />

            <Text style={styles.signatureText}>
              School Stamp
            </Text>

            <Text style={styles.signatureSubtext}>
              Official Authorization
            </Text>
          </View>
        </View>

        {/* CONFIDENTIALITY NOTICE */}

        <View
          style={styles.confidentiality}
          wrap={false}
        >
          <Text style={styles.confidentialityText}>
            This report is confidential and intended for official
            school use only. Student rankings reflect the records
            available for the selected academic period.
          </Text>
        </View>

        {/* EVERY-PAGE FOOTER */}

        <View style={styles.pageFooter} fixed>
          <View style={{ width: "65%" }}>
            <Text style={styles.footerBrand}>
              {SYSTEM_NAME}
            </Text>

            <Text style={styles.footerLeft}>
              {SCHOOL_NAME} • Student Ranking Report
            </Text>
          </View>

          <Text
            style={styles.footerRight}
            render={({ pageNumber, totalPages }) =>
              `PAGE ${pageNumber} OF ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
};

export default TopStudentPerPeriodPDF;
